import { Component, computed, inject, input, output, signal } from '@angular/core';
import { NgSwitch, NgSwitchCase, NgSwitchDefault, NgTemplateOutlet } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { TenantService } from '../../../core/services/tenant.service';
import { MenuItem } from '../models/menu.model';

/**
 * Um item do menu lateral. Resolve rota e estado ativo a partir do tenant
 * ativo — o mesmo padrão do antigo SideNavigationComponent (ARC-053).
 *
 * Submenu (ADR-012, cadeia Sidebar → Módulo → Submenu → Página): quando
 * `item().children` tem itens, este componente vira um agrupador — não
 * navega ao clicar, alterna expandir/recolher a lista de filhos (renderizada
 * reaproveitando este mesmo componente, já que filhos nunca têm netos). Com
 * a sidebar recolhida (ícones) não há espaço para a lista expandida — clicar
 * no agrupador navega direto para o primeiro filho.
 */
@Component({
  selector: 'app-sidebar-menu-item',
  standalone: true,
  imports: [RouterLink, NgSwitch, NgSwitchCase, NgSwitchDefault, NgTemplateOutlet],
  templateUrl: './sidebar-menu-item.component.html',
  styleUrl: './sidebar-menu-item.component.scss',
})
export class SidebarMenuItemComponent {
  private readonly router = inject(Router);
  private readonly tenantService = inject(TenantService);

  readonly item = input.required<MenuItem>();
  readonly collapsed = input<boolean>(false);
  readonly itemClick = output<void>();

  private readonly tenantId = computed(() => this.tenantService.activeTenantId());
  private readonly manuallyToggled = signal<boolean | null>(null);

  readonly hasChildren = computed(() => (this.item().children?.length ?? 0) > 0);

  readonly route = computed<string[]>(() => this.routeFor(this.item().routeSegment));

  readonly active = computed<boolean>(() => this.isActiveSegment(this.item().routeSegment));

  /** Algum filho é a página atual — usado para auto-expandir e destacar o pai. */
  readonly hasActiveChild = computed<boolean>(() =>
    (this.item().children ?? []).some(child => this.isActiveSegment(child.routeSegment))
  );

  readonly expanded = computed<boolean>(() => this.manuallyToggled() ?? this.hasActiveChild());

  /** Sidebar recolhida (ícones): agrupador sem espaço pra lista — navega direto ao 1º filho. */
  readonly firstChildRoute = computed<string[]>(() => {
    const firstChild = this.item().children?.[0];
    return firstChild ? this.routeFor(firstChild.routeSegment) : ['/'];
  });

  private routeFor(routeSegment: string): string[] {
    const tenantId = this.tenantId();
    if (!tenantId) return ['/'];
    // routeSegment pode conter múltiplos segmentos (ex.: 'cadastros/pessoas') —
    // cada trecho vira um segmento próprio para não ser codificado como %2F.
    return ['/', tenantId, ...routeSegment.split('/')];
  }

  private isActiveSegment(routeSegment: string): boolean {
    const tenantId = this.tenantId();
    if (!tenantId) return false;
    const targetPath = `/${tenantId}/${routeSegment}`;
    return this.router.isActive(targetPath, {
      paths: 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  toggleExpanded(): void {
    this.manuallyToggled.set(!this.expanded());
  }

  onClick(): void {
    this.itemClick.emit();
  }
}
